import { Paper, Typography, Box, LinearProgress, Divider, Card, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { DashboardFiltersDto, UpcomingEvent } from "../../../types/dashboard";
import { formatCurrencyShort } from "../../../utils/format";
import { MetricBarsSkeleton } from "./Skeletons";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

interface MetricsByEventProps {
  title: string;
  color: string;
  metricType: "billing" | "revenue" | "audience";
  events: UpcomingEvent[];
  loading: boolean;
  maxItems?: number;
  subtitle?: string;
}

const StyledPaper = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: 300,
  display: "flex",
  flexDirection: "column",
}));

const ChartTitle = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
});

const MetricRow = styled(Box)({
  display: "flex",
  gap: "0.5rem",
  marginBottom: "0.75rem",
  paddingRight: 8,
  position: "relative",
});

const MetricName = styled(Typography)({
  fontSize: "0.75rem",
  color: "#3E3E3E",
  width: 80,
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
});

const MetricValue = styled(Typography)({
  fontSize: "0.675rem",
  color: "#222222",
  minWidth: 60,
  textAlign: "right",
});

const StyledLinearProgress = styled(LinearProgress)({
  height: 16,
  borderRadius: 4,
  flex: 1,
  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
  },
});

export const MetricsByEvent = ({
  title,
  color,
  metricType,
  events,
  loading,
  maxItems = 50,
  subtitle,
}: MetricsByEventProps) => {
  const getMetricValue = (event: UpcomingEvent) => {
    if (metricType === "audience") {
      return event.audience || 0;
    } else if (metricType === "billing") {
      return event.billing || 0;
    } else {
      return event.revenue || 0;
    }
  };

  const renderValue = (event: UpcomingEvent) => {
    if (metricType === "audience") {
      return event.audience ? `${event.audience.toLocaleString()}` : "N/A";
    } else {
      return formatCurrencyShort(getMetricValue(event));
    }
  };

  const maxValue =
    events.length > 0 ? Math.max(...events.map((event) => getMetricValue(event))) : 1;

  // Ordenar eventos pelo valor da métrica selecionada, em ordem decrescente
  const sortedEvents = [...events]
    .sort((a, b) => getMetricValue(b) - getMetricValue(a))
    .slice(0, maxItems);

  return (
    <StyledPaper>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ChartTitle>{title}</ChartTitle>
          <DashboardSourceInfo
            context={title}
            hideExpectedNote={metricType === "audience"}
          />
        </Box>
        {subtitle && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <MetricBarsSkeleton />
      ) : sortedEvents.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum evento encontrado
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            overflowY: "auto",
            flex: 1,
            pr: 1,
          }}
        >
          {sortedEvents.map((event) => (
            <MetricRow
              key={event.id}
              sx={{
                cursor: "pointer",
                "&:hover": {
                  bgcolor: `${color}20`,
                },
              }}
            >
              <Tooltip 
                title={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {event.eventName}
                    </Typography>
                    <Typography variant="caption">
                      {event.clientName}
                    </Typography>
                  </Box>
                } 
                arrow 
                placement="top"
              >
                <MetricName>{event.eventName}</MetricName>
              </Tooltip>

              <Box position="relative" flex={1}>
                <StyledLinearProgress
                  variant="determinate"
                  value={(getMetricValue(event) / maxValue) * 100}
                  sx={{
                    backgroundColor: "rgba(0,0,0,0.05)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: color,
                    },
                  }}
                />
              </Box>

              <MetricValue>{renderValue(event)}</MetricValue>
            </MetricRow>
          ))}
        </Box>
      )}
    </StyledPaper>
  );
};
